import { computed } from "vue";
import { useRouteParams } from "@vueuse/router";
import { useExperienceTranslationStore } from "@/features/experience-translation/stores/useExperienceTranslationStore";
import { useTranslationTokenPermissions } from "@/features/token-permissions/composables/useTranslationTokenPermissions";
import type { AvailableLanguage } from "@/types/Language";
import { useComplexModelModelWithChanges } from "@/features/core-shared/composables/useComplexModelWithChanges";
import { translationHighlightArraySchema, translationHighlightSchema } from "@/features/experience-translation/schemas";
import { areCustomTranslationFieldsUpdated } from "@/features/experience-translation/sidebar/lib/field-update-helpers";
import type { CustomHighlightsKey, GenericHighlight, HighlightsKey } from "@/types/Highlights";
import {
  toPremadeRows,
  toPremadeRowsDiff,
  toCustomRows,
  toCustomRowsDiff,
  applyCustomRows,
  normalizeTranslationCustomHighlights,
} from "@/features/experience-shared/highlights-v2/translation/utils/translation-highlight-mappers";
import type {
  PremadeTranslationRow,
  CustomTranslationRow,
} from "@/features/experience-shared/highlights-v2/translation/types/highlight-translation";
import { useTranslationSourceHighlights } from "./useTranslationSourceHighlights";

type PremadeHighlightsKey =
  | "premade_highlights"
  | "premade_important_information"
  | "premade_included"
  | "premade_non_included";

export interface TranslationHighlightFieldConfig {
  fieldId: string;
  isRequired: boolean;
  /** Resolves the source/diff snapshot slice. */
  highlightKey: HighlightsKey;
  /** `translation.<key>` — editable custom list. */
  customKey: CustomHighlightsKey;
  /** `translation.<key>` — target premade list. */
  premadeKey: PremadeHighlightsKey;
}

/**
 * Unified per-field store for a translation highlight field (like curation's
 * `useCurationImportantInformation`): it both owns the editable `model` (the save flow reads it;
 * no write-back) and acts as the sidebar descriptor (`id`/`isRequired`/`isFilled`/
 * `hasUnpublishedChange`).
 */
export function createTranslationHighlightField(config: TranslationHighlightFieldConfig) {
  return () => {
    const translationStore = useExperienceTranslationStore();
    const { canEditTranslation } = useTranslationTokenPermissions();
    const { source, diffOld, hasDiff } = useTranslationSourceHighlights(config.highlightKey);
    const languageParam = useRouteParams<string>("language");

    const initialValue = computed<GenericHighlight[]>(
      () => (translationStore.translation[config.customKey] as GenericHighlight[]) ?? []
    );

    // Normalize so change detection ignores what the editor doesn't own (`action` tag, ordering,
    // backend-only fields); otherwise `hasChanges` stays stuck true after a save reload.
    const { model, hasChanges, reset } = useComplexModelModelWithChanges(initialValue, {
      normalizer: normalizeTranslationCustomHighlights,
    });

    const isReadonly = computed(() => !canEditTranslation());
    const isFilled = computed(() => translationHighlightArraySchema.safeParse(model.value).success);
    const isValid = computed(() => customRows.value.every((row) => row.error == null));
    const hasUnpublishedChange = computed(() => areCustomTranslationFieldsUpdated(config.customKey));
    const targetCountryCode = computed(() => (languageParam.value ?? "") as AvailableLanguage);

    const targetPremade = computed(() => (translationStore.translation[config.premadeKey] as GenericHighlight[]) ?? []);

    // Row projections for the stateless editor; the custom setter applies edits back onto the
    // model (updates existing records only, never creates).
    const premadeRows = computed<PremadeTranslationRow[]>(() =>
      hasDiff.value
        ? toPremadeRowsDiff(diffOld.value.premade, source.value.premade, targetPremade.value)
        : toPremadeRows(source.value.premade, targetPremade.value)
    );

    const customRows = computed<CustomTranslationRow[]>({
      get: () => {
        const rows = hasDiff.value
          ? toCustomRowsDiff(diffOld.value.custom, source.value.custom, model.value)
          : toCustomRows(source.value.custom, model.value);
        // The validation error lives in the store (like raw's field stores), not the row component.
        // Only editable rows can be fixed by the user, so only they carry an error.
        return rows.map((row) =>
          row.editable && !translationHighlightSchema.safeParse({ name: row.translation }).success
            ? { ...row, error: "experience.highlights.v2.translation.required" }
            : row
        );
      },
      set: (rows) => {
        model.value = applyCustomRows(rows, model.value);
      },
    });

    return {
      id: config.fieldId,
      customKey: config.customKey,
      model,
      premadeRows,
      customRows,
      targetCountryCode,
      hasDiff,
      isReadonly,
      isRequired: config.isRequired,
      isFilled,
      isValid,
      hasUnpublishedChange,
      hasChanges,
      reset,
    };
  };
}
